import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminPanel.css";
import AdminSidebar from "./AdminSidebar";
import AdministratorSidebar from "./AdministratorSidebar";
import UserSidebar from "./UserSidebar";
import { Table, Spinner } from 'react-bootstrap';

const AdminPanel = () => {
  const navigate = useNavigate();
  const [userType, setUserType] = useState("");
  const [userName, setUserName] = useState("");
  const [userFullName, setUserFullName] = useState("");
  const [userId, setUserId] = useState("");
  const [salesData, setSalesData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user data from localStorage
  useEffect(() => {
    const storedUserType = localStorage.getItem("usertype");
    const storedUserName = localStorage.getItem("userName");
    const storedFName = localStorage.getItem("fname");
    const storedLName = localStorage.getItem("lname");
    const storedUserId = localStorage.getItem("userId");

    console.log("LocalStorage Values:", {
      storedUserType,
      storedUserId,
      storedUserName,
      storedFName,
      storedLName,
    });

    if (!storedUserType || !storedUserId || !storedUserName) {
      console.warn("Missing critical localStorage data, redirecting to login");
      localStorage.clear();
      navigate("/login");
      return;
    }

    setUserType(storedUserType);
    setUserName(storedUserName);
    setUserId(storedUserId);
    if (storedFName && storedLName) {
      setUserFullName(`${storedFName} ${storedLName}`);
    } else if (storedFName) {
      setUserFullName(storedFName);
    } else {
      setUserFullName(storedUserName);
    }
  }, [navigate]);

  // Fetch sales data only when userType and userId are set
  useEffect(() => {
    if (!userType || !userId) {
      console.log("Waiting for userType and userId to be set:", { userType, userId });
      return;
    }
    console.log("Fetching sales data with:", { userType, userId });
    fetchSalesData();
  }, [userType, userId]);

  const fetchSalesData = async () => {
    setLoading(true);
    setError(null);
    console.log("fetchSalesData - userType:", userType, "userId:", userId);
    try {
      let url = "https://api.saadmotors.com/sales-by-user.php";
      if (userType === "user" && userId) {
        url += `?client_id=${encodeURIComponent(userId)}&usertype=${encodeURIComponent(userType)}`;
        console.log("Fetching data for user with client_id:", userId);
      } else {
        url += `?usertype=${encodeURIComponent(userType)}`;
        console.log("Fetching all data for administrator/admin");
      }
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! Status: ${response.status} - ${errorText}`);
      }
      const data = await response.json();
      console.log("API Response:", JSON.stringify(data, null, 2));
      if (Array.isArray(data)) {
        const groupedData = data.reduce((acc, sale) => {
          const clientName = sale.client_name || `User_${sale.client_id || sale.id}`;
          acc[clientName] = acc[clientName] || [];
          acc[clientName].push(sale);
          return acc;
        }, {});
        console.log("Grouped Data:", JSON.stringify(groupedData, null, 2));
        setSalesData(groupedData);
      } else if (data.message) {
        setError(data.message);
      } else {
        setSalesData({});
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setError(error.message || "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.clear(); // Ensure all data is cleared
    navigate("/login");
  };

  if (loading) return (
    <div className="admin-container" style={{ justifyContent: 'center', alignItems: 'center' }}>
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    </div>
  );
  if (error) return <div className="admin-container" style={{ padding: '20px', color: '#e74c3c', textAlign: 'center' }}>Error: {error}</div>;

  return (
    <div className="admin-container">
      <div className="sidebar-container">
        {userType === "administrator" && <AdministratorSidebar />}
        {userType === "admin" && <AdminSidebar />}
        {userType === "user" && <UserSidebar />}
      </div>

      <div className="admin-content">
        <div className="adminnavbar">
          <h1>Welcome to the Admin Panel, {userFullName}</h1>
          <button onClick={logout} className="logoutbtn">
            Logout
          </button>
        </div>

        <div className="mainsection">
          <h3>Dashboard Overview</h3>

          <div className="dashboard-charts">
            {Object.keys(salesData).length === 0 ? (
              <p style={{ textAlign: 'center', color: '#7f8c8d', padding: '20px' }}>No sales data available.</p>
            ) : (
              Object.keys(salesData).map((clientName) => {
                const clientSales = salesData[clientName];
                const totalBalance = clientSales.reduce((sum, sale) => {
                  const balance = sale.balance || 0;
                  return sum + (typeof balance === 'number' ? balance : parseFloat(balance) || 0);
                }, 0);

                return (
                  <div key={clientName} className="chart-container">
                    <h4>{clientName}'s Vehicle Inventory</h4>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Stock ID</th>
                          <th>Maker</th>
                          <th>Model</th>
                          <th>Chassis</th>
                          <th>Price</th>
                          <th>Allocated Amount</th>
                          <th>Percentage</th>
                          <th>Balance</th>
                          <th>Due Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientSales.map((sale) => (
                          <tr key={sale.id}>
                            <td>{sale.sale_date || 'N/A'}</td>
                            <td>{sale.stock_id || 'N/A'}</td>
                            <td>{sale.maker || 'N/A'}</td>
                            <td>{sale.model || 'N/A'}</td>
                            <td>{sale.vin || 'N/A'}</td>
                            <td>{sale.price || 0}</td>
                            <td>{sale.allocated_amount || 0}</td>
                            <td>{Math.round(sale.percentage || 0)}%</td> {/* Remove decimals and add % */}
                            <td>{sale.balance || 0}</td>
                            <td>{sale.due_date || 'N/A'}</td>
                          </tr>
                        ))}
                        <tr>
                          <td colSpan="8" style={{ fontWeight: 'bold', textAlign: 'right' }}>Total Balance</td>
                          <td style={{ fontWeight: 'bold' }}>
                            {typeof totalBalance === 'number' ? `$${totalBalance.toFixed(2)}` : '$0.00'}
                          </td>
                          <td></td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;